<template>
  <div class="flex flex-col justify-center h-full">
    <div class="text-center">
      <h2 class="text-2xl font-bold mb-5 uppercase primary-text dark:text-gray-100">
        {{ $t("Login.login") }}
      </h2>
    </div>

    <form @submit="onSubmit">
      <div class="mb-6">
        <input v-model="form.phone" type="text" id="Phone" :placeholder="$t('Login.phonePlaceholder')" required
          class="w-full h-12 py-3 px-4 rounded-lg border border-gray-300 dark:border-gray-600 text-black focus:outline-none focus:ring-2 focus:ring-blue-500" />
      </div>

      <!-- Password Input -->
      <div class="mb-6 relative">
        <input v-model="form.password" :type="showPassword ? 'text' : 'password'" id="Password" minlength="8"
          :placeholder="$t('Login.passwordPlaceholder')" required
          class="w-full h-[3rem] py-3 px-4 rounded-lg border border-gray-300 dark:border-gray-600 text-black focus:outline-none focus:ring-2 focus:ring-blue-500" />
        <button type="button" class="absolute right-3 top-1/2 -translate-y-1/2" @click="showPassword = !showPassword">
          <EyeSlashIcon v-if="showPassword" class="h-5 w-5 text-gray-500 dark:text-gray-400" />
          <EyeIcon v-else class="h-5 w-5 text-gray-500 dark:text-gray-400" />
        </button>
      </div>



      <!-- Submit Button -->
      <button
        type="submit"
        :disabled="form.isLoginLoading"
        class="w-full text-xl primary-btn rounded-lg justify-center !hover:scale-0 font-bold disabled:cursor-not-allowed disabled:opacity-70"
      >
        {{ $t("Login.loginBtn") }}
      </button>
    </form>

    <Geetest product="bind" ref="geetestRef" @success="handleGeetestSuccess" />

  </div>
</template>

<script setup lang="ts">
import { reactive, ref } from "vue";
import type { ComponentPublicInstance } from "vue";
import { EyeIcon, EyeSlashIcon } from "@heroicons/vue/24/outline";
import { loginAPI, type GeetestValidate } from "@/api/auth/auth";
import toast from "@/utils/toaster";
import { UserRole } from "@/types/auth/role";
import Geetest from "@/components/Geetest.vue";

const emit = defineEmits<{
  (e: "onLoginSuccess", token: string, username: string, userRole?: number): void;
}>();

interface LoginForm {
  phone: string;
  password: string;
  isLoginLoading: boolean;
}

interface GeetestInstance extends ComponentPublicInstance {
  reset: () => void;
  showCaptcha: () => boolean;
}

const form = reactive<LoginForm>({
  phone: "",
  password: "",
  isLoginLoading: false,
});

const showPassword = ref(false);
const geetestRef = ref<GeetestInstance | null>(null);

const onSubmit = async (event: Event) => {
  event.preventDefault();
  if (form.isLoginLoading) return;
  if (!form.phone || !form.password) {
    return;
  }

  const captchaShown = geetestRef.value?.showCaptcha();
  if (!captchaShown) {
    toast.error("验证码初始化中，请稍后再试");
  }
};

const handleGeetestSuccess = async (validate: GeetestValidate) => {
  await handleLogin(validate);
};

const handleLogin = async (validate: GeetestValidate) => {
  if (!form.phone || !form.password || form.isLoginLoading) {
    return;
  }
  form.isLoginLoading = true;

  try {
    const res = await loginAPI(form.phone, form.password, validate);
    if (!res.data || res.data.user.role !== UserRole.SUPER_ADMIN) {
      toast.error("账号或密码错误");
      return;
    }

    emit(
      "onLoginSuccess",
      res.data.token.accessToken,
      res.data.user.username,
      res.data.user.role
    );
  } catch (err: any) {
    toast.error("账号或密码错误");
  } finally {
    form.isLoginLoading = false;
    geetestRef.value?.reset();
  }
};
</script>

<style scoped>
input {
  color: black;
}

.dark input {
  color: white;
  background-color: #333333;
  border-color: #555555;
}
</style>
